import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchAllRankings, type CategoryRanking, type RankingEntry } from "../api/tcg";
import { useLanguage } from "../i18n/language.context";
import { fireConfetti } from "../utils/confetti.util";
import "./winners.page.css";

function ordinal(n: number, lang: string): string {
    if (lang === "en") {
        if (n === 1) return "1st";
        if (n === 2) return "2nd";
        if (n === 3) return "3rd";
        return `${n}th`;
    }

    if (lang === "de") return `${n}.`;

    return `${n}º`;
}

function CategoryWinners({ ranking }: { ranking: CategoryRanking }) {
    const { t: tr, lang } = useLanguage();
    const isManualCategory = ranking.group_key !== "product" && ranking.group_key !== "community";
    const topEntries = ranking.entries
        .filter((e) => e.rank >= 1 && e.rank <= 5)
        .sort((a, b) => a.rank - b.rank);
    const first = topEntries.find((e) => e.rank === 1);
    const rest = isManualCategory ? [] : topEntries.filter((e) => e.rank > 1);

    if (!first) return null;

    return (
        <section className="winners-category">
            <div className="winners-cat-head">
                <span className="winners-cat-line" />
                <h2>{ranking.name}</h2>
                <span className="winners-cat-line" />
            </div>

            <div className="winners-podium">
                <div className="winner-card-first">
                    <span className="winner-place-badge">{ordinal(1, lang)} {tr("ui.winners.place")}</span>

                    <div className="winner-image">
                        {first.image_url
                            ? <img src={first.image_url} alt={first.display_text} />
                            : <span className="winner-image-placeholder">{tr("ui.winners.picture")}</span>}
                    </div>

                    <span className="winner-first-name">{first.display_text}</span>
                    {first.game && <span className="winner-first-game">{first.game}</span>}
                    {!isManualCategory && (
                        <span className="winner-first-points">{first.points} {tr("ui.winners.pts")}</span>
                    )}
                    {first.description && (
                        <p className="winner-first-description">{first.description}</p>
                    )}
                </div>

                {rest.length > 0 && (
                    <div className="winner-runner-grid">
                        {rest.map((e: RankingEntry) => (
                            <div key={e.rank} className="winner-card-runner">
                                <span className="winner-rest-place">{ordinal(e.rank, lang)} {tr("ui.winners.place")}</span>

                                <div className="winner-runner-image">
                                    {e.image_url
                                        ? <img src={e.image_url} alt={e.display_text} />
                                        : <span className="winner-image-placeholder">{tr("ui.winners.picture")}</span>}
                                </div>

                                <span className="winner-rest-name">{e.display_text}</span>
                                <span className="winner-rest-points">{e.points} {tr("ui.winners.pts")}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

function WinnersPage() {
    const { slug } = useParams<{ slug?: string }>();
    const { t: tr, lang } = useLanguage();
    const [rankings, setRankings] = useState<CategoryRanking[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fireConfetti();
    }, [slug]);

    useEffect(() => {
        setLoading(true);
        fetchAllRankings(lang)
            .then((data) => setRankings(data))
            .catch((err) => setError(err instanceof Error ? err.message : tr("ui.common.loadError")))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lang]);

    const shown = slug ? rankings.filter((r) => r.slug === slug) : rankings;
    const title = slug && shown.length > 0 ? shown[0].name : tr("ui.winners.title");

    return (
        <div className="winners-page">
            <h1 className="winners-title">{title}</h1>

            {loading && <p className="winners-note">{tr("ui.common.loading")}</p>}
            {error && <p className="winners-note">{error}</p>}

            {!loading && !error && shown.length === 0 && (
                <p className="winners-note">{tr("ui.winners.pending")}</p>
            )}

            {shown.map((r) => (
                <CategoryWinners key={r.categoryId} ranking={r} />
            ))}

            <div className="winners-back-row">
                <Link to="/#award-categories" className="voting-back-link">
                    {tr("ui.nom.backAll")}
                </Link>
            </div>
        </div>
    );
}

export default WinnersPage;